import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import config from "../../config";
import { formatApiError } from "../../utilities/apiErrorMessages";
import { coalesceAsync } from "../../utilities/coalesceAsync";

export const GetTransactions = createAsyncThunk(
    "transactions/GetTransactions",
    async ({ page = 1, token, filters, perPage }, thunkAPI) => {
        const { rejectWithValue, fulfillWithValue } = thunkAPI;

        try {
            const url = new URL(`${config.apiUrl}/transactions`);
            url.searchParams.set('page', page);
            if (perPage && Number.isFinite(Number(perPage))) {
                url.searchParams.set('per_page', String(perPage));
            }

            if (filters && typeof filters === 'object') {
                Object.entries(filters).forEach(([k, v]) => {
                    if (v === undefined || v === null || v === '') {
                        return;
                    }
                    if (Array.isArray(v)) {
                        if (v.length === 0) return;
                        url.searchParams.set(k, v.join(','));
                        return;
                    }
                    url.searchParams.set(k, String(v));
                });
            }

            if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.debug('[GetTransactions] url=', url.toString());
            }

            const data = await coalesceAsync(
                `get-transactions:${url.toString()}`,
                async () => {
                    const response = await fetch(url.toString(), {
                        method: "GET",
                        headers: {
                            Accept: "application/json",
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    });

                    const body = await response.json();

                    if (!response.ok) {
                        const error = new Error(body?.message ?? "Unable to fetch transactions");
                        error.payload = body;
                        throw error;
                    }

                    return body;
                },
                { ttlMs: 1500 }
            );

            return fulfillWithValue(data.data);
        } catch (error) {
            if (error?.payload) {
                return rejectWithValue(error.payload);
            }
            return rejectWithValue({ message: error?.message ?? "Unable to fetch transactions" });
        }
    }
);

const initialState = {
    transactions: null,
    loading: false,
    error: null,
    totalTransactions: 0,
};

export const TransactionsSlice = createSlice({
    name: "transactions",
    initialState,
    reducers: {},
    extraReducers: {
        [GetTransactions.pending]: (state) => {
            state.loading = true;
            state.error = null;
        },
        [GetTransactions.fulfilled]: (state, action) => {
            state.loading = false;
            state.transactions = action.payload;
            state.error = null;

            let total = 0;
            const payload = action.payload;

            if (Array.isArray(payload)) {
                total = payload.length;
            } else if (payload && typeof payload === "object" && Array.isArray(payload.data)) {
                total = payload.total ?? payload.data.length;
            }

            state.totalTransactions = total;
        },
        [GetTransactions.rejected]: (state, action) => {
            state.loading = false;
            state.error = formatApiError(action.payload, action.error?.message ?? "");
        },
    },
});

export default TransactionsSlice.reducer;
